import React, { forwardRef } from "react";

// ----------------------------------------------------------------------

// Minimal alpha utility to convert hex/rgb colors to rgba with opacity
function alpha(color, opacity) {
    if (typeof color !== "string") return color;
    // If already rgba, just replace the alpha
    if (color.startsWith("rgba")) {
        const parts = color
            .replace(/^rgba\(/, "")
            .replace(/\)$/, "")
            .split(",")
            .map((v) => v.trim());
        if (parts.length === 4) {
            return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${opacity})`;
        }
    }
    // If rgb, append opacity
    if (color.startsWith("rgb")) {
        const parts = color
            .replace(/^rgb\(/, "")
            .replace(/\)$/, "")
            .split(",")
            .map((v) => v.trim());
        if (parts.length === 3) {
            return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${opacity})`;
        }
    }
    // Handle hex #RRGGBB or #RGB
    let hex = color.replace("#", "");
    if (hex.length === 3) {
        hex = hex
            .split("")
            .map((c) => c + c)
            .join("");
    }
    if (hex.length !== 6) return color;
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

// Simple palette fallback to mimic prior MUI values
const defaultPalette = {
    mode: "light",
    text: { primary: "#212121" },
    common: { white: "#fff" },
    grey: { 500: "#9e9e9e" },
    primary: {
        main: "#c50000",
        contrastText: "#fff",
        dark: "#7f0000",
        light: "#ff5131",
    },
    secondary: {
        main: "#9c27b0",
        contrastText: "#fff",
        dark: "#6a0080",
        light: "#d05ce3",
    },
    info: {
        main: "#0288d1",
        contrastText: "#fff",
        dark: "#005b9f",
        light: "#5eb8ff",
    },
    success: {
        main: "#2e7d32",
        contrastText: "#fff",
        dark: "#005005",
        light: "#60ad5e",
    },
    warning: {
        main: "#ed6c02",
        contrastText: "#fff",
        dark: "#b53d00",
        light: "#ff9d3f",
    },
    error: {
        main: "#d32f2f",
        contrastText: "#fff",
        dark: "#9a0007",
        light: "#ff6659",
    },
};

function getBaseStyles(palette) {
    return {
        height: 24,
        minWidth: 22,
        lineHeight: 0,
        borderRadius: 6,
        cursor: "default",
        alignItems: "center",
        whiteSpace: "nowrap",
        display: "inline-flex",
        justifyContent: "center",
        textTransform: "capitalize",
        padding: "0px 8px", // theme.spacing(0,1)
        fontSize: "0.75rem", // 12px
        fontWeight: 700, // theme.typography.fontWeightBold
    };
}

export const StyledLabel = forwardRef(function StyledLabel(
    {
        component = "span",
        ownerState = {},
        sx = {},
        style = {},
        palette = defaultPalette,
        children,
        ...other
    },
    ref
) {
    const isLight = palette.mode === "light";
    const filledVariant = ownerState.variant === "filled";
    const outlinedVariant = ownerState.variant === "outlined";
    const softVariant = ownerState.variant === "soft";

    const base = getBaseStyles(palette);

    const defaultStyle = {
        ...(ownerState.color === "default" && {
            ...(outlinedVariant && {
                backgroundColor: "transparent",
                color: palette.text.primary,
                border: `1px solid ${alpha(palette.grey[500], 0.32)}`,
            }),
            ...(softVariant && {
                color: isLight ? palette.text.primary : palette.common.white,
                backgroundColor: alpha(palette.grey[500], 0.16),
            }),
        }),
    };

    const colorKey = ownerState.color;
    const pickedPalette = palette[colorKey] || {};

    const colorStyle = {
        ...(ownerState.color &&
            ownerState.color !== "default" && {
                ...(filledVariant && {
                    color: pickedPalette.contrastText || "#fff",
                    backgroundColor: pickedPalette.main,
                }),
                ...(outlinedVariant && {
                    backgroundColor: "transparent",
                    color: pickedPalette.main,
                    border: `1px solid ${pickedPalette.main}`,
                }),
                ...(softVariant && {
                    color:
                        pickedPalette[isLight ? "dark" : "light"] ||
                        pickedPalette.main,
                    backgroundColor: alpha(pickedPalette.main || "#000", 0.16),
                }),
            }),
    };

    const mergedStyle = {
        ...base,
        ...defaultStyle,
        ...colorStyle,
        ...(sx || {}),
        ...(style || {}),
    };

    const ComponentTag = component || "span";
    return (
        <ComponentTag ref={ref} style={mergedStyle} {...other}>
            {children}
        </ComponentTag>
    );
});
